import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  FileText,
  GraduationCap,
  Layers,
  Search,
  Video,
  X
} from 'lucide-react';

import { useLearnerSearchQuery } from '@lok/services/learner';

// CON-306: search across the courses, modules, lessons and lesson
// resources the signed-in user can open (names only). A navbar button that
// opens a dropdown panel, as in the Figma Make prototype. Ctrl+K opens it
// too (the parent passes `openSignal`).

const TYPE_STYLE = {
  course: { label: 'Course', icon: GraduationCap, tone: 'text-sky-600 dark:text-sky-400 bg-sky-100 dark:bg-sky-900/30' },
  module: { label: 'Module', icon: Layers, tone: 'text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30' },
  lesson: { label: 'Lesson', icon: BookOpen, tone: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30' },
  resource: { label: 'Resource', icon: FileText, tone: 'text-primary-green bg-primary-green/15' }
};

const styleFor = (item) => {
  if (item.type === 'resource' && item.resourceType === 'video') {
    return { ...TYPE_STYLE.resource, icon: Video, label: 'Video' };
  }
  return TYPE_STYLE[item.type] || TYPE_STYLE.resource;
};

const useDebounced = (value, ms) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

const Highlight = ({ text, query }) => {
  const q = query.trim();
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className='bg-primary-green/40 text-inherit rounded-sm'>
        {text.slice(i, i + q.length)}
      </mark>
      {text.slice(i + q.length)}
    </>
  );
};

const LearnerSearch = ({ openSignal = 0 }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  // The words as typed, after "Search instead for" undid a spelling fix.
  const [exactQuery, setExactQuery] = useState('');
  const debounced = useDebounced(query.trim(), 250);
  const enabled = debounced.length >= 2;
  const exact = enabled && exactQuery === debounced;
  const { data, isFetching, isError } = useLearnerSearchQuery(
    { q: debounced, exact },
    { skip: !isOpen || !enabled }
  );
  const results = enabled ? data?.results || [] : [];
  // Only once the typed text has settled, so a banner for the previous
  // query never shows during the debounce.
  const settled = enabled && query.trim() === debounced;
  const correctedQuery = settled ? data?.corrected_query : null;
  const suggestion = settled ? data?.suggestion : null;
  const highlightQuery = correctedQuery || query;

  // Opened pre-filled from elsewhere in the app (Goal Progress "Find related
  // lessons" dispatches this event).
  const pendingQueryRef = useRef('');
  useEffect(() => {
    const onOpen = (e) => {
      pendingQueryRef.current = e.detail?.query || '';
      setIsOpen(false);
      setTimeout(() => setIsOpen(true), 0);
    };
    window.addEventListener('continuum:open-search', onOpen);
    return () => window.removeEventListener('continuum:open-search', onOpen);
  }, []);

  useEffect(() => {
    if (openSignal) setIsOpen(true);
  }, [openSignal]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery(pendingQueryRef.current);
    pendingQueryRef.current = '';
    setActiveIdx(0);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    const onDown = (e) => {
      if (!panelRef.current?.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [isOpen]);

  useEffect(() => setActiveIdx(0), [debounced]);

  const open = (item) => {
    setIsOpen(false);
    // A resource goes to its lesson first: the lesson page opens its
    // Resources tab and highlights this resource, and the learner opens it
    // from there (Dane, CON-306, the Figma Make search flow).
    if (item.type === 'resource' && item.resourceUrl) {
      navigate(item.url, { state: { searchResourceUrl: item.resourceUrl } });
      return;
    }
    navigate(item.url);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[activeIdx]) {
      open(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Spelling help from the backend (Dane, CON-306): results already shown for
  // the fixed words, or a "Did you mean" link when the typed words found some.
  const spellingLinkClass =
    'font-semibold text-primary-green underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-green/70 rounded';
  let spelling = null;
  if (correctedQuery && !isFetching) {
    spelling = (
      <p className='px-3 py-2 text-xs text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-800/60' aria-live='polite'>
        Showing results for <span className='font-semibold italic'>{correctedQuery}</span>.{' '}
        Search instead for{' '}
        <button
          type='button'
          className={spellingLinkClass}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setExactQuery(debounced)}
        >
          {query.trim()}
        </button>
      </p>
    );
  } else if (suggestion && !isFetching) {
    spelling = (
      <p className='px-3 py-2 text-xs text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-800/60' aria-live='polite'>
        Did you mean{' '}
        <button
          type='button'
          className={spellingLinkClass}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setQuery(suggestion)}
        >
          {suggestion}
        </button>
        ?
      </p>
    );
  }

  let body;
  if (!enabled) {
    body = (
      <div className='px-3 py-4 text-center'>
        <p className='text-xs text-gray-500 dark:text-gray-400'>
          Type to search across your courses, modules, lessons &amp; resources
        </p>
        <div className='flex items-center justify-center gap-3 mt-3 text-xs'>
          <span className='flex items-center gap-1 text-blue-600 dark:text-blue-400'><BookOpen className='w-3 h-3' /> Lessons</span>
          <span className='flex items-center gap-1 text-violet-600 dark:text-violet-400'><Layers className='w-3 h-3' /> Modules</span>
          <span className='flex items-center gap-1 text-primary-green'><FileText className='w-3 h-3' /> Resources</span>
        </div>
      </div>
    );
  } else if (isError) {
    body = <p className='px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400'>Search is not available right now.</p>;
  } else if (!results.length) {
    body = (
      <div className='flex flex-col items-center justify-center py-8 gap-1.5 text-center px-4'>
        <Search className='w-7 h-7 text-gray-300 dark:text-gray-600' />
        <p className='text-sm text-gray-500 dark:text-gray-400'>
          {isFetching ? 'Searching…' : <>No results for <span className='font-medium text-gray-700 dark:text-gray-300'>&ldquo;{query.trim()}&rdquo;</span></>}
        </p>
      </div>
    );
  } else {
    body = results.map((item, idx) => {
      const style = styleFor(item);
      const Icon = style.icon;
      return (
        <button
          key={item.id}
          type='button'
          role='option'
          aria-selected={idx === activeIdx}
          onMouseDown={(e) => {
            e.preventDefault();
            open(item);
          }}
          onMouseEnter={() => setActiveIdx(idx)}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
            idx === activeIdx ? 'bg-gray-50 dark:bg-gray-800/70' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
          } ${idx > 0 ? 'border-t border-gray-100 dark:border-gray-800/60' : ''}`}
        >
          <span className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${style.tone}`}>
            <Icon className='w-3.5 h-3.5' />
          </span>
          <span className='flex-1 min-w-0'>
            <span className='block text-sm font-medium text-gray-900 dark:text-white truncate leading-tight'>
              <Highlight text={item.title} query={highlightQuery} />
            </span>
            {(item.breadcrumb || item.minutes) && (
              <span className='block text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5'>
                {item.breadcrumb}
                {item.minutes ? <span className='text-gray-400'> · {item.minutes} min</span> : null}
              </span>
            )}
          </span>
          <span className='flex items-center gap-1.5 flex-shrink-0'>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${style.tone}`}>{style.label}</span>
            {idx === activeIdx && <ChevronRight className='w-3.5 h-3.5 text-gray-400' />}
          </span>
        </button>
      );
    });
  }

  return (
    <div className='relative' ref={panelRef}>
      <button
        type='button'
        onClick={() => setIsOpen((v) => !v)}
        className='flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full transition-colors bg-primary-green hover:bg-primary-green/90 text-primary-green-fg'
        aria-label='Search courses'
        aria-expanded={isOpen}
        title='Search (Ctrl+K)'
      >
        <Search className='h-4 w-4 md:h-5 md:w-5' aria-hidden='true' />
      </button>

      {isOpen && (
        <div className='fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-16 sm:top-auto sm:mt-2 z-[60] sm:w-96 bg-white dark:bg-[#1D2838] rounded-xl shadow-2xl border border-gray-200/80 dark:border-gray-700/60 overflow-hidden'>
          <div className='flex items-center gap-2.5 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700/60'>
            <Search className='w-4 h-4 text-gray-400 flex-shrink-0' />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder='Search lessons, modules, resources…'
              aria-label='Search lessons, modules, resources'
              className='flex-1 bg-transparent text-sm text-gray-900 dark:text-white outline-none border-0 focus:ring-0 p-0 placeholder:text-gray-400 dark:placeholder:text-gray-500'
            />
            {query ? (
              <button
                type='button'
                aria-label='Clear search'
                onMouseDown={(e) => {
                  e.preventDefault();
                  setQuery('');
                }}
                className='p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400'
              >
                <X className='w-3.5 h-3.5' />
              </button>
            ) : (
              <kbd className='text-[10px] px-1.5 py-0.5 text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded'>ESC</kbd>
            )}
          </div>
          {spelling}
          <div role='listbox' className='max-h-[360px] overflow-y-auto overscroll-contain'>
            {body}
          </div>
        </div>
      )}
    </div>
  );
};

export default LearnerSearch;
